import React, { useState, useEffect, useMemo, useCallback, useRef } from "react";
import { Download, FileSpreadsheet, Eye, CalendarDays, Users, Target, UserPlus, FileText, BarChart3, History, Loader2, Search, Award, Wallet, Receipt, Monitor, HelpCircle, LogOut, ShieldCheck, BookOpen, Network } from "lucide-react";
import api from "../api";
import { K } from "../theme";
import { Bt, Cd, Sl, SearchSelect } from "../components";
import { ActivityLog } from "../components/ActivityLog";
import { notify } from "../notify";
import { fmtDMY } from "../dateFormat";

const CAT_ICON = { leave: CalendarDays, attendance: Users, recruitment: Target, onboarding: UserPlus, employee: FileText, activity: History, appraisal: Award, payroll: Wallet, expenses: Receipt, assets: Monitor, helpdesk: HelpCircle, offboarding: LogOut, compliance: ShieldCheck, policies: BookOpen, organization: Network };
// Same order as the sidebar.
const CAT_ORDER = ["leave", "attendance", "appraisal", "payroll", "expenses", "assets", "policies", "employee", "organization", "onboarding", "offboarding", "recruitment", "compliance"];
const CAT_COLOR = { leave: K.g, attendance: K.bl, recruitment: K.pu, onboarding: K.a, employee: K.ro, activity: K.p, appraisal: K.pu, payroll: K.g, expenses: K.a, assets: K.cy, helpdesk: K.bl, offboarding: K.r, compliance: K.tl, policies: K.p, organization: K.ro };

const pad = (n) => String(n).padStart(2, "0");
const ymd = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
const PRESETS = [
  ["Today", () => { const t = new Date(); return [ymd(t), ymd(t)]; }],
  ["Last 7 days", () => { const t = new Date(); const f = new Date(); f.setDate(t.getDate() - 6); return [ymd(f), ymd(t)]; }],
  ["This month", () => { const t = new Date(); return [ymd(new Date(t.getFullYear(), t.getMonth(), 1)), ymd(t)]; }],
  ["Last month", () => { const t = new Date(); return [ymd(new Date(t.getFullYear(), t.getMonth() - 1, 1)), ymd(new Date(t.getFullYear(), t.getMonth(), 0))]; }],
  ["This quarter", () => { const t = new Date(); const q = Math.floor(t.getMonth() / 3) * 3; return [ymd(new Date(t.getFullYear(), q, 1)), ymd(t)]; }],
  ["Last 6 months", () => { const t = new Date(); return [ymd(new Date(t.getFullYear(), t.getMonth() - 5, 1)), ymd(t)]; }],
  ["This year", () => { const t = new Date(); return [ymd(new Date(t.getFullYear(), 0, 1)), ymd(t)]; }],
];

const dateInput = "px-[10px] py-[7px] bg-bg2 border border-b0 rounded-[10px] text-[14px] text-t0 outline-none";
const Label = ({ children }) => <div className="text-[11.5px] font-extrabold uppercase tracking-wider text-t3 mb-[5px]">{children}</div>;
const showCell = (c, v) => {
  if (v === null || v === undefined || v === "") return "";
  if (c.type === "date") return fmtDMY(String(v).length === 10 ? v + "T12:00:00" : v);
  return String(v);
};

// ─── charts (hand-rolled SVG — the app has no chart library) ────────────────
const PALETTE = [K.p, K.g, K.a, K.pu, K.ro, K.cy, K.tl, "#64748B"];
const COLOR = { p: K.p, g: K.g, r: K.r, a: K.a, bl: K.bl, pu: K.pu, ro: K.ro, cy: K.cy, tl: K.tl };
const colorOf = (c, i = 0) => COLOR[c] || PALETTE[i % PALETTE.length];
const hasData = (c) => (c.data ? c.data.some((d) => d.value) : c.series?.some((s) => s.data.some((d) => d.value)));
const fmt = (v) => (typeof v === "number" ? v.toLocaleString("en-IN") : v);

// Entrance + hover motion. Keyframes read CSS variables so every bar / arc can
// animate to its own size; reduced-motion users get the final state at once.
const CHART_CSS = `
@keyframes rpGrowY { from { transform: scaleY(0); } to { transform: scaleY(1); } }
@keyframes rpGrowX { from { transform: scaleX(0); } to { transform: scaleX(1); } }
@keyframes rpArc { from { stroke-dasharray: 0 var(--c); } to { stroke-dasharray: var(--l) var(--r); } }
@keyframes rpRoll { from { transform: rotate(-200deg) scale(.7); opacity: 0; } to { transform: rotate(0) scale(1); opacity: 1; } }
@keyframes rpDraw { from { stroke-dashoffset: 1; } to { stroke-dashoffset: 0; } }
@keyframes rpFade { from { opacity: 0; } to { opacity: 1; } }
@keyframes rpPop { from { opacity: 0; transform: translateY(10px); } to { opacity: 1; transform: translateY(0); } }
.rp-bar { transform-origin: bottom; animation: rpGrowY .8s cubic-bezier(.2,.8,.2,1) both; transition: filter .15s, transform .15s; cursor: pointer; }
.rp-bar:hover { filter: brightness(1.12) saturate(1.2); transform: scaleY(1.04); }
.rp-hbar { transform-origin: left; animation: rpGrowX .9s cubic-bezier(.2,.8,.2,1) both; transition: filter .15s; }
.rp-hrow { transition: background .15s; border-radius: 8px; cursor: pointer; }
.rp-hrow:hover { background: rgba(37,99,235,.07); }
.rp-hrow:hover .rp-hbar { filter: brightness(1.12) saturate(1.2); }
.rp-donut-roll { transform-origin: center; animation: rpRoll 1s cubic-bezier(.2,.8,.2,1) both; }
.rp-arc { animation: rpArc .9s cubic-bezier(.3,.7,.2,1) both; transition: stroke-width .15s, opacity .15s; cursor: pointer; }
.rp-line { stroke-dasharray: 1; animation: rpDraw 1.4s ease-out both; }
.rp-dot { animation: rpFade .4s ease-out both; transition: r .15s; cursor: pointer; }
.rp-dot:hover { r: 6; }
.rp-card { animation: rpPop .5s ease-out backwards; }
@media (prefers-reduced-motion: reduce) { .rp-bar, .rp-hbar, .rp-arc, .rp-line, .rp-donut-roll, .rp-dot, .rp-card { animation: none !important; } .rp-line { stroke-dasharray: none; } }
`;

// Number that counts up to its value ("₹6,28,042", "27.3%", "104").
const CountUp = ({ value }) => {
  const [shown, setShown] = useState(value);
  useEffect(() => {
    const m = /^([^\d-]*)(-?[\d,]*\.?\d+)(.*)$/.exec(String(value));
    if (!m || window.matchMedia?.("(prefers-reduced-motion: reduce)").matches) { setShown(value); return undefined; }
    const target = parseFloat(m[2].replace(/,/g, ""));
    const dec = (m[2].split(".")[1] || "").length;
    const indian = m[1].includes("₹");
    let raf; const t0 = performance.now();
    const step = (t) => {
      const k = Math.min(1, (t - t0) / 900); const e = 1 - Math.pow(1 - k, 3);
      const v = target * e;
      const txt = indian || target >= 1000 ? v.toLocaleString("en-IN", { minimumFractionDigits: dec, maximumFractionDigits: dec }) : v.toFixed(dec);
      setShown(`${m[1]}${txt}${m[3]}`);
      if (k < 1) raf = requestAnimationFrame(step); else setShown(value);
    };
    raf = requestAnimationFrame(step);
    return () => cancelAnimationFrame(raf);
  }, [value]);
  return <>{shown}</>;
};

// Folds the long tail of a pie into "Others" so slices always add up to the total.
const withOthers = (data, n = 7) => (data.length <= n + 1 ? data : [...data.slice(0, n), { label: "Others", value: Math.round(data.slice(n).reduce((a, x) => a + x.value, 0) * 10) / 10 }]);

const Donut = ({ data: raw, unit, size = 176, tip }) => {
  const data = withOthers(raw);
  const total = data.reduce((s, d) => s + d.value, 0) || 1;
  const [hi, setHi] = useState(null);
  const r = size / 2 - 14; const circ = 2 * Math.PI * r;
  let off = 0;
  const show = (e, d, i) => { setHi(i); tip.show(e, <><b>{d.label}</b><br />{fmt(d.value)} {unit || ""} · {((d.value / total) * 100).toFixed(1)}%</>); };
  return (
    <div className="flex flex-wrap items-center gap-5">
      <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} className="flex-shrink-0 rp-donut-roll">
        <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke={K.bg3} strokeWidth="22" />
        {data.map((d, i) => {
          const len = (d.value / total) * circ;
          const el = (
            <circle key={d.label} className="rp-arc" cx={size / 2} cy={size / 2} r={r} fill="none" stroke={PALETTE[i % PALETTE.length]}
              strokeWidth={hi === i ? 28 : 22} opacity={hi == null || hi === i ? 1 : 0.45}
              strokeDashoffset={-off} transform={`rotate(-90 ${size / 2} ${size / 2})`}
              style={{ "--l": `${len}px`, "--r": `${circ - len}px`, "--c": `${circ}px`, strokeDasharray: `${len} ${circ - len}`, animationDelay: `${i * 90}ms` }}
              onMouseMove={(e) => show(e, d, i)} onMouseLeave={() => { setHi(null); tip.hide(); }} />
          );
          off += len; return el;
        })}
        <text x="50%" y="48%" textAnchor="middle" className="fill-t0" style={{ fontSize: 22, fontWeight: 800 }}>{hi != null ? fmt(data[hi].value) : fmt(Math.round(total * 10) / 10)}</text>
        <text x="50%" y="62%" textAnchor="middle" className="fill-t3" style={{ fontSize: 11, fontWeight: 600 }}>{hi != null ? `${((data[hi].value / total) * 100).toFixed(1)}%` : unit || "total"}</text>
      </svg>
      <div className="space-y-[3px] min-w-[150px] flex-1">
        {data.map((d, i) => (
          <div key={d.label} className="flex items-center gap-2 text-[13px] px-2 py-[3px] rounded-lg cursor-pointer transition-colors" style={{ background: hi === i ? `${PALETTE[i % PALETTE.length]}18` : "transparent" }}
            onMouseEnter={() => setHi(i)} onMouseLeave={() => setHi(null)}>
            <span className="w-[10px] h-[10px] rounded-full flex-shrink-0" style={{ background: PALETTE[i % PALETTE.length] }} />
            <span className="text-t1 truncate max-w-[170px]" title={d.label}>{d.label}</span>
            <span className="ml-auto font-bold text-t0">{fmt(d.value)}</span>
            <span className="text-t3 w-[46px] text-right">{((d.value / total) * 100).toFixed(1)}%</span>
          </div>
        ))}
      </div>
    </div>
  );
};

const HBars = ({ data, color, tip }) => {
  const max = Math.max(...data.map((d) => d.value), 1);
  const total = data.reduce((s, d) => s + d.value, 0) || 1;
  return (
    <div className="space-y-[2px]">
      {data.map((d, i) => (
        <div key={d.label} className="rp-hrow flex items-center gap-3 text-[13px] px-1 py-[3px]"
          onMouseMove={(e) => tip.show(e, <><b>{d.label}</b><br />{fmt(d.value)} · {((d.value / total) * 100).toFixed(1)}% of shown</>)} onMouseLeave={tip.hide}>
          <div className="w-[130px] truncate text-t1" title={d.label}>{d.label}</div>
          <div className="flex-1 h-[16px] bg-bg2 rounded-full overflow-hidden">
            <div className="rp-hbar h-full rounded-full" style={{ width: `${(d.value / max) * 100}%`, background: colorOf(color), animationDelay: `${i * 70}ms` }} />
          </div>
          <div className="w-[44px] text-right font-bold text-t0">{fmt(d.value)}</div>
        </div>
      ))}
    </div>
  );
};

const Bars = ({ series, height = 150, tip }) => {
  const labels = series[0].data.map((d) => d.label);
  const max = Math.max(...series.flatMap((s) => s.data.map((d) => d.value)), 1);
  return (
    <div>
      <div className="flex flex-wrap gap-3 mb-1 min-h-[4px]">
        {series.length > 1 && series.map((s, i) => <span key={s.name} className="flex items-center gap-[6px] text-[12px] text-t2"><span className="w-[10px] h-[10px] rounded-sm" style={{ background: colorOf(s.color, i) }} />{s.name}</span>)}
      </div>
      <div className="overflow-x-auto">
        <div className="flex items-end gap-2 pt-4" style={{ height: height + 56, minWidth: labels.length * (series.length * 18 + 22) }}>
          {labels.map((l, li) => (
            <div key={l + li} className="flex-1 flex flex-col items-center justify-end h-full min-w-[34px]">
              <div className="flex items-end gap-[3px] w-full justify-center" style={{ height: height + 18 }}>
                {series.map((s, i) => {
                  const v = s.data[li]?.value || 0;
                  return (
                    <div key={s.name} className="flex flex-col items-center justify-end h-full">
                      <div className="text-[11px] font-bold text-t0 mb-[2px]" style={{ animation: "rpFade .6s ease-out both", animationDelay: `${300 + li * 60}ms` }}>{v ? fmt(v) : ""}</div>
                      <div className="rp-bar rounded-t-md" style={{ width: series.length > 1 ? 16 : 30, height: Math.max((v / max) * height, v ? 3 : 1), background: colorOf(s.color, i), opacity: v ? 1 : 0.25, animationDelay: `${li * 60}ms` }}
                        onMouseMove={(e) => tip.show(e, <><b>{l}</b><br />{series.length > 1 ? `${s.name}: ` : ""}{fmt(v)}</>)} onMouseLeave={tip.hide} />
                    </div>
                  );
                })}
              </div>
              <div className="text-[11.5px] text-t2 font-semibold mt-[6px] text-center leading-tight max-w-[70px] break-words">{l}</div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

const Line = ({ series, tip }) => {
  const W = 640; const H = 190; const L = 34; const B = 26; const T = 10;
  const labels = series[0].data.map((d) => d.label);
  const max = Math.max(...series.flatMap((s) => s.data.map((d) => d.value)), 1);
  const x = (i) => L + (labels.length < 2 ? (W - L) / 2 : (i / (labels.length - 1)) * (W - L - 8));
  const y = (v) => T + (1 - v / max) * (H - T - B);
  const every = Math.ceil(labels.length / 10);
  return (
    <div>
      <div className="flex flex-wrap gap-3 mb-1">{series.map((s, i) => <span key={s.name} className="flex items-center gap-[6px] text-[12px] text-t2"><span className="w-[10px] h-[3px] rounded" style={{ background: colorOf(s.color, i) }} />{s.name}</span>)}</div>
      <svg viewBox={`0 0 ${W} ${H}`} className="w-full h-auto">
        {[0, 0.25, 0.5, 0.75, 1].map((g) => <g key={g}><line x1={L} x2={W - 8} y1={y(max * g)} y2={y(max * g)} stroke={K.b0} strokeDasharray="3 3" /><text x={L - 6} y={y(max * g) + 3} textAnchor="end" style={{ fontSize: 10 }} className="fill-t3">{Math.round(max * g * 10) / 10}</text></g>)}
        {series.map((s, si) => (
          <g key={s.name}>
            <polyline className="rp-line" pathLength="1" fill="none" stroke={colorOf(s.color, si)} strokeWidth="2.4" strokeLinejoin="round" strokeLinecap="round" points={s.data.map((d, i) => `${x(i)},${y(d.value)}`).join(" ")} style={{ animationDelay: `${si * 200}ms` }} />
            {s.data.length <= 60 && s.data.map((d, i) => (
              <circle key={i} className="rp-dot" cx={x(i)} cy={y(d.value)} r="3.2" fill={colorOf(s.color, si)} style={{ animationDelay: `${600 + i * 25}ms` }}
                onMouseMove={(e) => tip.show(e, <><b>{d.label}</b><br />{s.name}: {fmt(d.value)}</>)} onMouseLeave={tip.hide} />
            ))}
          </g>
        ))}
        {labels.map((l, i) => (i % every === 0 ? <text key={i} x={x(i)} y={H - 8} textAnchor="middle" style={{ fontSize: 10 }} className="fill-t3">{l}</text> : null))}
      </svg>
    </div>
  );
};

// One tooltip per chart card; follows the pointer.
const ChartCard = ({ c, i = 0 }) => {
  const [t, setT] = useState(null);
  const tip = useMemo(() => ({
    show: (e, content) => setT({ x: e.clientX, y: e.clientY, content }),
    hide: () => setT(null),
  }), []);
  return (
    <div className="rp-card" style={{ animationDelay: `${i * 70}ms` }}>
      <Cd>
        <div className="text-[15px] font-extrabold text-t0 mb-3">{c.title}</div>
        {!hasData(c) ? <div className="py-8 text-center text-t3 text-[13px]">No data for these filters</div>
          : c.type === "donut" ? <Donut data={c.data} unit={c.unit} tip={tip} />
            : c.type === "hbars" ? <HBars data={c.data} color={c.color} tip={tip} />
              : c.type === "bars" ? <Bars series={[{ name: c.title, color: c.color, data: c.data }]} tip={tip} />
                : c.type === "groupbars" ? <Bars series={c.series} tip={tip} />
                  : <Line series={c.series} tip={tip} />}
      </Cd>
      {t && (
        <div className="fixed z-[9999] pointer-events-none px-3 py-2 rounded-lg text-[12.5px] leading-snug text-white shadow-lg"
          style={{ left: Math.min(t.x + 14, window.innerWidth - 220), top: t.y + 16, background: "rgba(15,23,42,.94)", maxWidth: 220 }}>{t.content}</div>
      )}
    </div>
  );
};

const TONE = { good: K.g, bad: K.r, warn: K.a };
const Kpis = ({ items }) => (
  <div className="grid gap-4 grid-cols-2 lg:grid-cols-5 mb-4">
    {items.map((k, i) => (
      <div key={k.label} className="rp-card" style={{ animationDelay: `${i * 60}ms` }}>
        <div className="bg-s rounded-2xl border border-b0 p-5 shadow-card transition-all duration-150 hover:shadow-card-hover hover:-translate-y-[2px] h-full">
          <div className="text-[12px] font-extrabold uppercase tracking-wide text-t3">{k.label}</div>
          <div className="text-[28px] font-extrabold leading-tight mt-1" style={{ color: TONE[k.tone] || K.t0 }}><CountUp value={k.value} /></div>
          {k.sub && <div className="text-[12px] text-t3 mt-[2px]">{k.sub}</div>}
        </div>
      </div>
    ))}
  </div>
);

// ─── page ───────────────────────────────────────────────────────────────────
export const ReportsPage = () => {
  const [catalog, setCatalog] = useState(null);
  const [err, setErr] = useState("");
  const [cat, setCat] = useState(null);
  const [view, setView] = useState("dashboard");
  const [reportId, setReportId] = useState("");
  const [f, setF] = useState({ company: "", office: "", department: "", employee: "", q: "", leaveType: "" });
  const [from, setFrom] = useState(PRESETS[5][1]()[0]);
  const [to, setTo] = useState(PRESETS[5][1]()[1]);
  const [date, setDate] = useState(ymd(new Date()));
  const [params, setParams] = useState({});
  const [inactive, setInactive] = useState(false);
  const [busy, setBusy] = useState("");
  const [preview, setPreview] = useState(null);
  const [dash, setDash] = useState(null);
  const [dashLoading, setDashLoading] = useState(false);
  const seq = useRef(0);
  const [dashRun, setDashRun] = useState(0);
  const set = (k) => (v) => setF((s) => ({ ...s, [k]: v }));

  useEffect(() => {
    api.get("/api/reports/catalog").then((c) => {
      if (!c) return;
      setCatalog(c);
      const first = [...c.categories].sort((a, b) => CAT_ORDER.indexOf(a.id) - CAT_ORDER.indexOf(b.id))[0];
      setCat(first?.id || (c.activity ? "activity" : null));
    }).catch((e) => setErr(e.message));
  }, []);

  const category = catalog?.categories.find((c) => c.id === cat);
  const report = category?.reports.find((r) => r.id === reportId);
  const isActivity = cat === "activity";
  const nameSearch = cat === "recruitment" || cat === "onboarding"; // candidates aren't employees — search by name

  useEffect(() => { if (category && !category.reports.some((r) => r.id === reportId)) setReportId(category.reports[0]?.id || ""); setPreview(null); setErr(""); }, [category]); // eslint-disable-line
  useEffect(() => {
    if (!report) return;
    const init = {};
    (report.params || []).forEach((p) => { init[p.key] = p.default !== undefined && p.default !== null ? String(p.default) : (p.options && p.required ? String(p.options.find((o) => o[0] !== "")?.[0] ?? "") : ""); });
    setParams(init); setPreview(null);
    // Reports with one column per day only take a short range — shrink it instead of failing.
    if (report.maxDays && report.dateMode === "range") {
      const days = Math.round((new Date(to + "T12:00:00") - new Date(from + "T12:00:00")) / 86400000) + 1;
      if (days > report.maxDays) { const f = new Date(to + "T12:00:00"); f.setDate(f.getDate() - (report.maxDays - 1)); setFrom(ymd(f)); }
    }
  }, [reportId]); // eslint-disable-line

  const filterQs = useCallback((rangeNeeded, singleNeeded) => {
    const q = new URLSearchParams();
    if (f.company) q.set("company", f.company);
    if (f.office) q.set("office", f.office);
    if (f.department && !nameSearch) q.set("department", f.department);
    if (f.employee && !nameSearch) q.set("employee", f.employee);
    if (f.q && nameSearch) q.set("q", f.q);
    if (f.leaveType && cat === "leave") q.set("leave_type", f.leaveType);
    if (rangeNeeded) { q.set("from", from); q.set("to", to); }
    if (singleNeeded) q.set("date", date);
    if (inactive) q.set("inactive", "1");
    return q;
  }, [f, from, to, date, inactive, cat, nameSearch]);

  // Dashboard: reload whenever a filter changes.
  useEffect(() => {
    if (!cat || isActivity || view !== "dashboard") return;
    const mine = ++seq.current;
    setDashLoading(true); setErr("");
    const t = setTimeout(() => {
      api.get(`/api/reports/dashboard/${cat}?${filterQs(true, false)}`)
        .then((d) => { if (mine === seq.current && d) { setDash(d); setDashRun((n) => n + 1); } })
        .catch((e) => { if (mine === seq.current) { setErr(e.message); setDash(null); } })
        .finally(() => { if (mine === seq.current) setDashLoading(false); });
    }, 250);
    return () => clearTimeout(t);
  }, [cat, view, filterQs, isActivity]);

  const reportQs = useCallback((extra = {}) => {
    const q = filterQs(report?.dateMode === "range", report?.dateMode === "single");
    q.set("report", reportId);
    if (!report?.leaveFilter) q.delete("leave_type");
    Object.entries(params).forEach(([k, v]) => { if (v !== "") q.set("p_" + k, v); });
    Object.entries(extra).forEach(([k, v]) => q.set(k, v));
    return q.toString();
  }, [filterQs, report, reportId, params]);

  const missing = (report?.params || []).find((p) => p.required && !params[p.key]);
  const runPreview = async () => {
    setBusy("preview"); setErr("");
    try { const r = await api.get(`/api/reports/run?${reportQs()}`); if (r) setPreview(r); }
    catch (e) { setErr(e.message); setPreview(null); }
    finally { setBusy(""); }
  };
  const download = async (format) => {
    setBusy(format); setErr("");
    try { await api.download(`/api/reports/download?${reportQs({ format })}`, `report.${format}`); }
    catch (e) { setErr(e.message); notify(e.message, { title: "Could not download", variant: "error" }); }
    finally { setBusy(""); }
  };

  const opts = useMemo(() => ({
    company: [{ v: "", l: "All companies" }, ...(catalog?.companies || []).map((c) => ({ v: String(c.id), l: c.name }))],
    office: [{ v: "", l: "All locations" }, ...(catalog?.offices || []).map((o) => ({ v: String(o.id), l: o.name }))],
    department: [{ v: "", l: "All departments" }, ...(catalog?.departments || []).map((d) => ({ v: String(d.id), l: d.name }))],
    leaveType: [{ v: "", l: "All leave types" }, ...(catalog?.leaveTypes || [])],
    employee: (catalog?.employees || []).map((e) => ({ v: e.id, l: e.name + (e.active ? "" : " (inactive)"), sub: e.id })),
  }), [catalog]);

  if (!catalog) return <div className="p-8 text-t3 text-[14px]">{err || "Loading reports…"}</div>;
  const pills = [...[...catalog.categories].sort((a, b) => CAT_ORDER.indexOf(a.id) - CAT_ORDER.indexOf(b.id)).map((c) => ({ id: c.id, label: c.label })), ...(catalog.activity ? [{ id: "activity", label: "Activity Log" }] : [])];
  if (!pills.length) return <Cd><div className="text-center py-10 text-t3 text-[14px]">You don't have access to any reports yet. Ask an admin to grant it under Settings → Permissions → Reports.</div></Cd>;

  const showRange = view === "dashboard" || report?.dateMode === "range";
  const color = CAT_COLOR[cat] || K.p;

  return (
    <div>
      <style>{CHART_CSS}</style>
      <div className="mb-4">
        <h1 className="m-0 text-2xl font-extrabold text-t0">Reports</h1>
        <div className="text-[14px] text-t3 mt-[3px]">Dashboards with graphs for each area, and filtered CSV / Excel downloads.</div>
      </div>

      <div className="flex flex-wrap gap-2 mb-4">
        {pills.map((c) => {
          const Ic = CAT_ICON[c.id] || FileText; const on = c.id === cat;
          return (
            <button key={c.id} onClick={() => { if (c.id !== cat) { setCat(c.id); setDash(null); } }}
              className={`flex items-center gap-2 px-4 py-[9px] rounded-xl text-[14px] font-bold cursor-pointer border transition-colors ${on ? "text-white border-transparent" : "bg-s border-b0 text-t1 hover:border-p"}`}
              style={on ? { background: CAT_COLOR[c.id] || K.p } : undefined}>
              <Ic size={15} />{c.label}
            </button>
          );
        })}
      </div>

      {isActivity ? <ActivityLog module="all" title="Activity Log — all modules" showModuleFilter canDownload={!!catalog.activityDownload} /> : <>
        <Cd style={{ marginBottom: 16 }}>
          <div className="flex flex-wrap items-end gap-4">
            <div className="w-[200px]"><Label>Company</Label><Sl options={opts.company} value={f.company} onChange={set("company")} /></div>
            <div className="w-[200px]"><Label>Location / Centre</Label><Sl options={opts.office} value={f.office} onChange={set("office")} /></div>
            {!nameSearch && <div className="w-[190px]"><Label>Department</Label><Sl options={opts.department} value={f.department} onChange={set("department")} /></div>}
            {!nameSearch
              ? <div className="w-[230px]"><Label>Person</Label><SearchSelect options={opts.employee} value={f.employee} onChange={set("employee")} placeholder="All people" searchPlaceholder="Search name or ID…" clearable /></div>
              : <div className="w-[230px]"><Label>Name contains</Label>
                <div className="relative"><Search size={13} className="absolute left-[10px] top-1/2 -translate-y-1/2 text-t3" /><input className={dateInput + " w-full pl-[30px]"} placeholder="Candidate / joiner name" value={f.q} onChange={(e) => set("q")(e.target.value)} /></div></div>}
            {cat === "leave" && (view === "dashboard" || report?.leaveFilter) && <div className="w-[190px]"><Label>Leave category</Label><Sl options={opts.leaveType} value={f.leaveType} onChange={set("leaveType")} /></div>}
          </div>
          <div className="flex flex-wrap items-end gap-4 mt-4">
            {showRange && <>
              <div><Label>From</Label><input type="date" className={dateInput} value={from} max={to} onChange={(e) => setFrom(e.target.value)} /></div>
              <div><Label>To</Label><input type="date" className={dateInput} value={to} min={from} onChange={(e) => setTo(e.target.value)} /></div>
              <div className="flex flex-wrap gap-[6px] pb-[2px]">
                {PRESETS.map(([l, fn]) => <button key={l} onClick={() => { const [a, b] = fn(); setFrom(a); setTo(b); }} className="px-[10px] py-[5px] rounded-full text-[12px] font-bold cursor-pointer border border-b0 bg-s text-t2 hover:text-p hover:border-p">{l}</button>)}
              </div>
            </>}
            {showRange && view === "download" && report?.maxDays && <div className="text-[12px] text-t3 pb-[8px]">This report shows one column per day — up to {report.maxDays} days.</div>}
            {!showRange && report?.dateMode === "single" && <div><Label>Date</Label><input type="date" className={dateInput} value={date} onChange={(e) => setDate(e.target.value)} /></div>}
            {!showRange && report?.dateMode === "none" && <div className="text-[12.5px] text-t3 pb-[8px]">This report is a live snapshot — no date needed.</div>}
            <label className="flex items-center gap-2 text-[13px] text-t1 font-semibold pb-[8px] cursor-pointer ml-auto">
              <input type="checkbox" checked={inactive} onChange={(e) => setInactive(e.target.checked)} /> Include inactive / exited employees
            </label>
          </div>
        </Cd>

        <div className="inline-flex p-1 bg-bg2 rounded-xl gap-[2px] mb-4">
          {[["dashboard", "Dashboard & graphs", BarChart3], ["download", "Download reports", FileSpreadsheet]].map(([k, l, Ic]) => (
            <button key={k} onClick={() => setView(k)}
              className={`px-4 py-[7px] rounded-lg text-[13px] font-bold flex items-center gap-[6px] cursor-pointer border-0 ${view === k ? "bg-s text-t0 shadow-[0_1px_3px_rgba(0,0,0,0.1)]" : "bg-transparent text-t2 hover:text-t0"}`}>
              <Ic size={14} />{l}
            </button>
          ))}
        </div>

        {err && <div className="text-[14px] text-r bg-rb border border-rd rounded-lg px-3 py-2 mb-3 font-medium">{err}</div>}

        {view === "dashboard" && (
          <div className={dashLoading ? "opacity-60 transition-opacity" : ""}>
            {!dash ? <Cd><div className="py-10 text-center text-t3 text-[14px]">{dashLoading ? "Building dashboard…" : "No data"}</div></Cd> : <>
              <Kpis key={`k${dashRun}`} items={dash.kpis} />
              <div className="grid gap-4 grid-cols-1 xl:grid-cols-2">{dash.charts.map((c, i) => <ChartCard key={`${c.id}-${dashRun}`} c={c} i={i} />)}</div>
            </>}
          </div>
        )}

        {view === "download" && category && <>
          <Cd style={{ marginBottom: 16 }}>
            <Label>Report</Label>
            <div className="grid gap-[10px] grid-cols-1 md:grid-cols-2 xl:grid-cols-3">
              {category.reports.map((r) => {
                const on = r.id === reportId;
                return (
                  <button key={r.id} onClick={() => setReportId(r.id)} className="text-left rounded-xl p-3 cursor-pointer border transition-colors"
                    style={{ borderColor: on ? color : K.b0, background: on ? `${color}12` : K.s }}>
                    <div className="text-[14px] font-extrabold text-t0">{r.name}</div>
                    <div className="text-[12.5px] text-t3 mt-[3px] leading-snug">{r.description}</div>
                  </button>
                );
              })}
            </div>
          </Cd>

          {report && (
            <Cd style={{ marginBottom: 16 }}>
              <div className="flex flex-wrap items-end gap-4">
                {(report.params || []).map((p) => (
                  <div key={p.key} className={p.type === "number" ? "w-[130px]" : "w-[230px]"}>
                    <Label>{p.label}{p.required ? " *" : ""}</Label>
                    {p.type === "select"
                      ? <Sl options={(p.options || []).map((o) => ({ v: o[0], l: o[1] }))} value={params[p.key] ?? ""} onChange={(v) => setParams((s) => ({ ...s, [p.key]: v }))} />
                      : <input type="number" min="1" className={dateInput + " w-full"} value={params[p.key] ?? ""} onChange={(e) => setParams((s) => ({ ...s, [p.key]: e.target.value }))} />}
                  </div>
                ))}
                <div className="flex flex-wrap gap-2 ml-auto">
                  <Bt v="secondary" icon={busy === "preview" ? Loader2 : Eye} onClick={runPreview} disabled={!!busy || !!missing}>Preview</Bt>
                  {category.canDownload && <>
                    <Bt v="secondary" icon={Download} onClick={() => download("csv")} disabled={!!busy || !!missing}>{busy === "csv" ? "Preparing…" : "Download CSV"}</Bt>
                    <Bt icon={FileSpreadsheet} onClick={() => download("xlsx")} disabled={!!busy || !!missing}>{busy === "xlsx" ? "Preparing…" : "Download as Excel"}</Bt>
                  </>}
                </div>
              </div>
              {!category.canDownload && <div className="text-[12.5px] text-t3 mt-2">You can preview this report. Downloading isn't enabled for your account.</div>}
              {missing && <div className="text-[12.5px] text-a mt-2 font-semibold">Choose “{missing.label}” to continue.</div>}
            </Cd>
          )}

          {preview && (
            <Cd>
              <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
                <div>
                  <div className="text-[16px] font-extrabold text-t0">{preview.report.name}</div>
                  <div className="text-[12.5px] text-t3 mt-[2px]">{preview.total} row{preview.total === 1 ? "" : "s"}{preview.truncated ? ` — showing the first ${preview.rows.length}; the download has everything` : ""}</div>
                </div>
                <div className="flex flex-wrap gap-[6px]">
                  {preview.meta.filter(([k]) => !["Report", "Generated"].includes(k)).map(([k, v]) => (
                    <span key={k} className="text-[11.5px] px-[9px] py-[3px] rounded-full bg-bg2 border border-b0 text-t2"><b className="text-t3">{k}:</b> {String(v)}</span>
                  ))}
                </div>
              </div>
              <div className="overflow-auto max-h-[520px] border border-b0 rounded-xl">
                <table className="w-full border-collapse text-[13px]">
                  <thead className="sticky top-0 z-[1]">
                    <tr>{preview.columns.map((c) => <th key={c.key} className={`px-3 py-[9px] text-[11.5px] font-extrabold uppercase tracking-wide text-white whitespace-nowrap ${c.type === "number" ? "text-right" : "text-left"}`} style={{ background: "#1E3A8A" }}>{c.label}</th>)}</tr>
                  </thead>
                  <tbody>
                    {preview.rows.map((r, i) => (
                      <tr key={i} className={i % 2 ? "bg-bg2/50" : ""}>
                        {preview.columns.map((c) => <td key={c.key} className={`px-3 py-[7px] border-b border-b0/60 ${c.type === "number" ? "text-right tabular-nums" : "whitespace-nowrap max-w-[320px] truncate"}`} title={String(r[c.key] ?? "")}>{showCell(c, r[c.key])}</td>)}
                      </tr>
                    ))}
                    {!preview.rows.length && <tr><td colSpan={preview.columns.length} className="text-center py-8 text-t3">No records for these filters.</td></tr>}
                  </tbody>
                </table>
              </div>
            </Cd>
          )}
        </>}
      </>}
    </div>
  );
};
